/* eslint-disable react/jsx-no-literals, react-hooks/rules-of-hooks */
import React, { useCallback, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"

import { Loader, LoaderSize } from "@izettle/react"

import { ModalProvider } from "@/core/components/modal"
import { Page, View, Progress } from "@/core/components/shared"
import {
  SliderConsumer,
  SliderProvider,
  SliderContextProps,
  focusFirstElement,
} from "@/core/components/slider"
import { FailureMessage } from "@/modules/sign-up/api/types"
import { EmptyState } from "@/modules/sign-up/components/empty-state"
import { ErrorState } from "@/modules/sign-up/components/error-state"
import Navigation from "@/modules/sign-up/components/navigation"
import { PolicyModal } from "@/modules/sign-up/components/policy-modal"
import { SendReceiptOptionsModal } from "@/modules/sign-up/components/send-receipt-options-modal"
import { SlideIndexs, Modals } from "@/modules/sign-up/components/types"
import {
  TrackingAction,
  useSignUpTracking,
} from "@/modules/sign-up/hooks/tracking-sign-up"

import { useLandingSlides } from "./use-landing-slides"

export const SignUpPage = () => {
  const ref = useRef(null)
  const { purchaseUuid, orgUuid } = useParams<{
    purchaseUuid: string
    orgUuid: string
  }>()

  if (!purchaseUuid || !orgUuid) {
    return <EmptyState />
  }

  const {
    orgName,
    orgLogoUrl,
    orgPhoneNumber,
    orgContactEmail,
    orgPrivacyPolicyLink,
    isFetching,
    isEditName,
    customerUuid,
    slides,
    customerEmail,
    customerPhone,
    failureMessage,
  } = useLandingSlides(purchaseUuid, orgUuid)

  const { track } = useSignUpTracking(purchaseUuid)

  useEffect(() => {
    track(TrackingAction.ViewedIntro)
  }, [])

  const modals = [
    <PolicyModal
      key={Modals.Policy}
      orgName={orgName}
      phone={orgPhoneNumber}
      email={orgContactEmail}
      linkToMerchantPrivacyPolicy={orgPrivacyPolicyLink}
    />,
    <SendReceiptOptionsModal
      key={Modals.ContactOptions}
      customerUuid={customerUuid}
      purchaseUuid={purchaseUuid}
      phone={customerPhone}
      email={customerEmail}
    />,
  ]

  const Header = useCallback(
    () => (
      <Navigation
        purchaseUuid={purchaseUuid}
        orgImageUrl={orgLogoUrl}
        orgPrivacyPolicyLink={orgPrivacyPolicyLink}
        slidesWithNoHeader={[
          SlideIndexs.Welcome,
          SlideIndexs.SignUpSuccess,
          SlideIndexs.ContactSent,
          SlideIndexs.ThankYou,
        ]}
        slidesToHideInfoIcon={[SlideIndexs.Confirm]}
        slidesToSkip={[SlideIndexs.Phone, SlideIndexs.RetrySendReceipt]}
      />
    ),
    [orgLogoUrl],
  )

  const Footer = useCallback(
    (props: SliderContextProps) => (
      <Progress
        {...props}
        startIndex={SlideIndexs.FirstName}
        endIndex={isEditName ? SlideIndexs.LastName : SlideIndexs.Email}
      />
    ),
    [isEditName],
  )

  if (isFetching) {
    return (
      <Page data-testid="loading">
        <Loader size={LoaderSize.Large} />
      </Page>
    )
  }

  if (failureMessage) {
    switch (failureMessage) {
      case FailureMessage.PURCHASE_NOT_FOUND:
        return <EmptyState />
      default:
        return <ErrorState />
    }
  }

  const onSlide = () => {
    if (ref?.current) {
      focusFirstElement(ref.current, ".swiper-slide-active .focus") // which is the back button always
    }
  }

  return (
    <Page>
      <View withShadow ref={ref}>
        <SliderProvider maxSlides={slides.length} onSlide={onSlide}>
          <ModalProvider modals={modals}>
            <SliderConsumer header={Header} footer={Footer}>
              {slides.map(({ Slide, props, key }) => (
                <div key={`slide-key-${key}`}>
                  <Slide {...props} slideNumber={key} />
                </div>
              ))}
            </SliderConsumer>
          </ModalProvider>
        </SliderProvider>
      </View>
    </Page>
  )
}
